import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, MessageSquare, LifeBuoy, Check, Loader2, BookOpen } from "lucide-react";
import { Logo } from "@/shared/brand";
import { useAuth } from "@/features/auth/context/AuthContext";
import { sendSupportMessage, type SupportCategory } from "@/features/support/supportApi";

const CATEGORIES: { value: SupportCategory; label: string }[] = [
  { value: "general", label: "General question" },
  { value: "sales", label: "Sales & plans" },
  { value: "billing", label: "Billing" },
  { value: "technical", label: "Technical help" },
  { value: "bug", label: "Report a bug" },
];

const CHANNELS = [
  { icon: Mail, title: "Email us", detail: "support@jewelitt.com — we reply within one business day." },
  { icon: LifeBuoy, title: "Priority support", detail: "Studio & Growth plans get faster, prioritised responses." },
  { icon: BookOpen, title: "Docs & guides", detail: "Set up S3, embeds, and configurators from the help centre." },
];

/** Public contact / support page. Works for guests and (pre-filled) signed-in users. */
export default function ContactPage() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [category, setCategory] = useState<SupportCategory>("general");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || message.trim().length < 5) {
      setError("Please add your name, email and a short message.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await sendSupportMessage({ name: name.trim(), email: email.trim(), category, subject: subject.trim() || undefined, message: message.trim() });
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send your message");
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full rounded-xl border border-white/60 bg-white/70 px-3.5 py-2.5 text-[14px] text-ink-900 outline-none backdrop-blur placeholder:text-ink-400 focus:border-[var(--accent-border,#d8c7a0)]";

  return (
    <div className="vault-surface min-h-screen text-ink-900">
      <header className="flex items-center gap-4 px-5 py-3 sm:px-10">
        <Link to="/"><Logo size="sm" /></Link>
        <Link to={user ? "/vault" : "/"} className="ml-auto flex items-center gap-1.5 rounded-full border border-white/60 bg-white/60 px-3.5 py-1.5 text-[13px] font-medium text-ink-700 backdrop-blur transition hover:text-ink-900">
          <ArrowLeft size={14} /> {user ? "Back to Vault" : "Home"}
        </Link>
      </header>

      <main className="mx-auto grid max-w-5xl gap-8 px-5 py-8 sm:px-10 lg:grid-cols-[1fr_1.1fr] lg:py-14">
        {/* editorial / channels */}
        <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
          <p className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold-600">
            <span className="h-px w-6 bg-gold-500/60" /> Contact & support
          </p>
          <h1 className="font-headline text-[34px] leading-[1.08] tracking-[-0.02em] text-ink-900">
            We're here to <span className="gold-gradient-text">help</span>.
          </h1>
          <p className="mt-3 max-w-md text-[14px] leading-relaxed text-ink-600">
            Questions about plans, embeds, storage, or a design that won't behave? Send us a note and a
            real person on the Jewelitt team will get back to you.
          </p>
          <div className="mt-8 space-y-3">
            {CHANNELS.map((c) => (
              <div key={c.title} className="surface-card flex items-start gap-3 rounded-2xl p-4">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[var(--accent-soft,#efe6d4)] text-[var(--accent-strong,#7c5f2e)]">
                  <c.icon size={17} />
                </span>
                <div>
                  <div className="text-[13.5px] font-semibold text-ink-900">{c.title}</div>
                  <div className="text-[12.5px] text-ink-600">{c.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* form */}
        <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.08 }} className="surface-card rounded-3xl p-6 sm:p-8">
          {sent ? (
            <div className="flex flex-col items-center justify-center py-14 text-center">
              <span className="mb-4 grid h-14 w-14 place-items-center rounded-full bg-emerald/10 text-emerald">
                <Check size={26} />
              </span>
              <h2 className="font-headline text-[22px] text-ink-900">Message sent</h2>
              <p className="mt-2 max-w-xs text-[13.5px] text-ink-600">
                Thanks, {name.split(" ")[0] || "there"} — we've got it and will reply to <b>{email}</b> within one business day.
              </p>
              <button onClick={() => { setSent(false); setMessage(""); setSubject(""); }} className="mt-6 rounded-full border border-white/60 bg-white/70 px-4 py-2 text-[13px] font-medium text-ink-800 backdrop-blur hover:text-ink-900">
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="mb-1 flex items-center gap-2 text-[13px] font-semibold text-ink-800">
                <MessageSquare size={15} className="text-[var(--accent-strong,#a0793d)]" /> Send us a message
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-ink-500">Name</span>
                  <input className={field} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-ink-500">Email</span>
                  <input className={field} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@studio.com" />
                </label>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-ink-500">Topic</span>
                  <select className={field} value={category} onChange={(e) => setCategory(e.target.value as SupportCategory)}>
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-ink-500">Subject <span className="font-normal text-ink-400">(optional)</span></span>
                  <input className={field} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Short summary" />
                </label>
              </div>
              <label className="block">
                <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-ink-500">Message</span>
                <textarea className={`${field} min-h-[130px] resize-y`} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="How can we help?" />
              </label>
              {error && <div className="rounded-lg bg-red/10 px-3 py-2 text-[12.5px] text-red">{error}</div>}
              <button type="submit" disabled={busy} className="btn-shine gold-gradient flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-[14px] font-semibold text-white shadow-[0_4px_14px_rgba(150,110,72,0.3)] transition hover:opacity-95 disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : null}
                {busy ? "Sending…" : "Send message"}
              </button>
              <p className="text-center text-[11.5px] text-ink-400">We only use your email to reply to this enquiry.</p>
            </form>
          )}
        </motion.section>
      </main>
    </div>
  );
}
